import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { Col, Row, Skeleton } from "antd";
import AdminHOC from "../../../components/shared/HOC/AdminHOC";
import { deleteCourseContent } from "../../../service/AdminService/BookService";
import { TAB_LIST } from "../../../assets/FormFeilds/Config/config";
import PageLoader from "../../../components/global/Loader/PageLoader";
import CustomTab from "../../../components/shared/tab/TabComponent";
import CommonTable from "../../../components/shared/table/Table";
import ConfirmDeletePopup from "../../../components/shared/confirmDeletePopup/ConfirmDeletePopup";
import { useCoursesContent } from "../../../hooks/CoursesHooks/useCoursesContent";
import CourseContentModal from "./components/CourseContentModal";
import ContentShowPage from "../../user/courses/ContentShowPage";
import CustomButton from "../../../components/shared/button/CustomButton";
import CustomInput from "../../../components/shared/Input/Custom-Input";
import LottieIcon from "../../../components/shared/lottieIcon/LottieIcon";
import VideoModal from "../../../components/admin/booksAdmin/VideoModal";
import PDFReaderModal from "../../../components/admin/booksAdmin/PDFReaderModal";
import usePagination from "../../../assets/hooks/PaginationHooks/usePagination";
import { getCourseContentColumns } from "../../../assets/FormFeilds/courseContentColumns";
import { showErrorMessage, showSuccessMessage } from "../../../components/shared/pagination/messages";
import backButton from "../../../assets/back-btn.svg";
import searchIconsvg from "../../../assets/icons/Search-Icon.svg";
import { debounceFunction } from "../../../assets/commonFunctions/CommonFunction";

const CourseContentAdmin = ({ setHideSidebar }) => {
    const { courseId } = useParams();
    const navigate = useNavigate();
    const tableRef = useRef(null);

    const { data, fetchCourseContent, name } = useCoursesContent();

    const {
        currentPage, setCurrentPage,
        totalElements, setTotalElements,
        pageSize, setPageSize,
    } = usePagination();

    const [uiState, setUiState] = useState({
        loading: false,
        loader: false,
        isInputHovered: false,
    });

    const [filterState, setFilterState] = useState({
        searchText: "",
        committedSearch: "",
        activeTab: "1",
        isActive: true,
    });

    const [modalState, setModalState] = useState({
        isModalOpen: false,
        selectedContent: null,
        isDeletePopupModal: false,
        idToDelete: null,
    });

    const [contentState, setContentState] = useState({
        showContent: false,
        selectedRecord: null,
        visiblePopover: null,
        hoveredButton: null,
    });

    const [mediaState, setMediaState] = useState({
        isVideoModalOpen: false,
        currentVideoFileName: "",
        currentContentType: "",
        isPDFModalOpen: false,
        currentPDFUrl: "",
    });

    const { loading, loader, isInputHovered } = uiState;
    const { searchText, activeTab, isActive } = filterState;
    const { isModalOpen, selectedContent, isDeletePopupModal, idToDelete } = modalState;
    const { showContent, selectedRecord } = contentState;
    const { isVideoModalOpen, currentVideoFileName, currentContentType, isPDFModalOpen, currentPDFUrl } = mediaState;

    const loadData = useCallback(async () => {
        setUiState(prev => ({ ...prev, loading: true }));
        try {
            const response = await fetchCourseContent(
                courseId,
                currentPage - 1,
                pageSize,
                filterState.isActive,
                filterState.committedSearch.trim(),
            );
            if (response) {
                setTotalElements(response.totalElements);
            }
        } catch {
            showErrorMessage("Failed to load content");
        } finally {
            setUiState(prev => ({ ...prev, loading: false }));
        }
    }, [courseId, currentPage, pageSize, filterState.isActive, filterState.committedSearch, fetchCourseContent, setTotalElements]);

    useEffect(() => {
        loadData();
        setHideSidebar(showContent);
    }, [showContent, loadData]);

    const debouncedApplySearch = useMemo(
        () => debounceFunction((value) => {
            setFilterState(prev => ({ ...prev, committedSearch: value }));
            setCurrentPage(1);
        }, 500),
        []
    );

    const handleSearch = useCallback((value) => {
        const normalized = value.toLowerCase();
        setFilterState(prev => ({ ...prev, searchText: normalized }));
        debouncedApplySearch(normalized);
    }, [debouncedApplySearch]);

    const handleEdit = useCallback((content) => {
        setModalState(prev => ({ ...prev, selectedContent: content, isModalOpen: true }));
    }, []);

    const handleDelete = useCallback((record) => {
        setModalState(prev => ({ ...prev, idToDelete: record.courseContentId, isDeletePopupModal: true }));
    }, []);


    const handleDeleteCourseContent = async () => {
        if (!modalState.idToDelete) return;
        setUiState(prev => ({ ...prev, loader: true }));
        try {
            await deleteCourseContent(modalState.idToDelete);
            showSuccessMessage("Content deleted successfully.");
            setModalState(prev => ({ ...prev, isDeletePopupModal: false, idToDelete: null }));
            loadData();
        } catch {
            showErrorMessage("Failed to delete content.");
        } finally {
            setUiState(prev => ({ ...prev, loader: false }));
        }
    };

    const handleAddNewCourseContent = () => {
        setModalState(prev => ({ ...prev, selectedContent: null, isModalOpen: true }));
    };

    const closeModal = () => {
        setModalState(prev => ({ ...prev, selectedContent: null, isModalOpen: false }));
    };
    const handleTableChange = useCallback((pagination) => {
        if (!pagination) return;
        if (pagination.pageSize !== pageSize) {
            setPageSize(pagination.pageSize);
            setCurrentPage(1);
        } else {
            setCurrentPage(pagination.current);
        }
        tableRef.current?.querySelector(".ant-table-body")?.scrollTo({ top: 0, behavior: "auto" });
    }, [pageSize, setPageSize, setCurrentPage]);

    const handleTabChange = useCallback((key) => {
        setFilterState(prev => ({ ...prev, activeTab: key, isActive: key === "1" }));
        setCurrentPage(1);
    }, [setCurrentPage]);

    const columns = useMemo(() => getCourseContentColumns({
        navigate,
        handleEdit,
        handleDelete,
        setShowContent: (val) => setContentState(prev => ({ ...prev, showContent: val })),
        setSelectedRecord: (val) => setContentState(prev => ({ ...prev, selectedRecord: val })),
        visiblePopover: contentState.visiblePopover,
        setVisiblePopover: (val) => setContentState(prev => ({ ...prev, visiblePopover: val })),
        hoveredButton: contentState.hoveredButton,
        setHoveredButton: (val) => setContentState(prev => ({ ...prev, hoveredButton: val })),
    }), [handleEdit, handleDelete, contentState.visiblePopover, contentState.hoveredButton]);

    return (
        <>
            {showContent ? (
                <ContentShowPage
                    items={data}
                    selectedRecord={selectedRecord}
                    setShowContent={(val) => setContentState(prev => ({ ...prev, showContent: val }))}
                    setSelectedRecord={(val) => setContentState(prev => ({ ...prev, selectedRecord: val }))}
                    courseId={courseId}
                />
            ) : (
                <>
                    <PageLoader visible={loader} />
                    <section className="w-full max-w-full h-[100vh] p-6 flex flex-col border border-[#E8E8E8] rounded-[10px] shadow-[0px_0px_6px_0px_rgba(0,0,0,0.05)]">
                        <header className="flex flex-col gap-6 mb-6">
                            <div className="flex justify-between">
                                <h1 className="text-[#E74860] text-[28px] font-medium">
                                    {!loading ? (
                                        `Content For ${name}`
                                    ) : (
                                        <Skeleton.Input active style={{ width: 220, height: 28, borderRadius: 6 }} />
                                    )}
                                </h1>
                                <div
                                    className="flex gap-2 justify-center cursor-pointer"
                                    onClick={() => navigate(-1)}
                                >
                                    <img alt="back-btn" src={backButton} />
                                    <span>Back</span>
                                </div>
                            </div>
                        </header>

                        <Row>
                            <Col span={6}>
                                <CustomTab
                                    entries={TAB_LIST}
                                    activeKey={activeTab}
                                    onChange={handleTabChange}
                                />
                            </Col>
                            <Col span={18}>
                                <div className="flex gap-[10px] justify-end">
                                    <div
                                        onMouseEnter={() => setUiState(prev => ({ ...prev, isInputHovered: true }))}
                                        onMouseLeave={() => setUiState(prev => ({ ...prev, isInputHovered: false }))}
                                    >
                                        <CustomInput
                                            icon={
                                                isInputHovered
                                                    ? <LottieIcon name="searchIcon" size={24} shouldPlay />
                                                    : <img src={searchIconsvg} alt="search" className="w-6 h-6" />
                                            }
                                            placeholder="Search Title"
                                            value={searchText}
                                            onChange={(e) => handleSearch(e.target.value)}
                                            className="w-[300px]"
                                            allowClear
                                        />
                                    </div>
                                    <CustomButton onClick={handleAddNewCourseContent}>
                                        Add Content
                                    </CustomButton>


                                    <CustomButton
                                        onClick={() => {
                                            navigate(
                                                `/books/course-content/content-report/${courseId}`,
                                            );
                                        }}
                                    >
                                        Report
                                    </CustomButton>
                                </div>
                            </Col>
                        </Row>

                        <div ref={tableRef}>
                            <CommonTable
                                data={data}
                                columns={columns}
                                showLoader={loading}
                                scroll={{ y: "55vh" }}
                                className="hide-scrollbar top-table-radius"
                                pagination={{
                                    current: currentPage,
                                    pageSize: pageSize,
                                    total: totalElements,
                                    showSizeChanger: true,
                                }}
                                onChange={handleTableChange}
                                serverSide
                            />
                        </div>
                    </section>

                    <VideoModal
                        isOpen={isVideoModalOpen}
                        fileName={currentVideoFileName}
                        resourceType={currentContentType}
                        onClose={() => setMediaState(prev => ({ ...prev, currentVideoFileName: "", isVideoModalOpen: false }))}
                    />
                    <PDFReaderModal
                        isOpen={isPDFModalOpen}
                        fileName={currentPDFUrl}
                        onClose={() => setMediaState(prev => ({ ...prev, currentPDFUrl: "", isPDFModalOpen: false }))}
                        blockTime={0}
                    />
                    <CourseContentModal
                        isModalOpen={isModalOpen}
                        handleCloseModal={closeModal}
                        selectedContent={selectedContent}
                        setLoading={(val) => setUiState(prev => ({ ...prev, loader: val }))}
                        loadContent={loadData}
                        courseId={courseId}
                        courseTitle={name}
                    />
                    <ConfirmDeletePopup
                        isOpen={isDeletePopupModal}
                        loading={loader}
                        onClose={() => setModalState(prev => ({ ...prev, isDeletePopupModal: false }))}
                        onConfirm={handleDeleteCourseContent}
                    />
                </>
            )}
        </>
    );
};

export default AdminHOC(CourseContentAdmin);
